import { useEffect, useRef, useState } from 'react';
import {
  createProductOrder,
  fetchAvailableInstallationDates,
  fetchCustomer,
  fetchDeliveryOptions,
  fetchProductDeliveryMethods,
  updateProductOrder,
  updateProductOrderDeliveryMethod,
} from '../../../common/fetch.js';
import { toDacInstallationDate } from '../../../common/installationDatesApiCoCo.js';
import { InstallationAvailabilityStatus } from '../../../common/types/installationDatesCoCo.js';
import { getCompanyName } from '../../../common/utils/accountUtils.js';
import {
  getCustomerPrimaryAddress,
  mapCustomerAddressToDeliveryAddress,
} from '../../../common/utils/customerAddressUtilsCoCo.js';
import { DeliveryOption } from '../../../generated/api/deliveryOption.js';
import { getActiveAccountMasterId } from '../../../selfservice/common/localStorageUtils.js';
import { PlaceOrderStep } from '../placeOrderTypes.js';
import { shouldShowInstallationDateSelection } from '../productConfig/productConfigRegistryCoCo.js';
import { flattenDeliveryOfferings } from '../utils/deliveryMethodUtilsCoCo.js';
import {
  buildDeliveryMethodRequest,
  buildProductOrderRequest,
  buildUpdateProductOrderRequest,
} from '../utils/orderMappingUtilsCoCo.js';
import type {
  AvailableInstallationDatesResponseItem,
  InstallationAvailabilityState,
} from '../../../common/types/installationDatesCoCo.js';
import type { ShoppingBasketType } from '../../../common/types/shoppingBasketCoCo.js';
import type { Contact } from '../../../generated/api/contact.js';
import type { DeliveryMethodGroup } from '../../../generated/api/deliveryMethodGroup.js';
import type { DeliveryOptionsResponse } from '../../../generated/api/deliveryOptionsResponse.js';
import type { ProductOfferingsResponse } from '../../../generated/api/productOfferingsResponse.js';
import type { AuthenticatedUser } from '../../../public/site/authState.js';
import type { CustomerPrimaryAddress } from '../placeOrderTypes.js';
import type { PlaceOrderFormDataCoCo } from '../placeOrderTypesCoCo.js';

export interface UseCheckoutOrchestratorOptions {
  basket: ShoppingBasketType;
  mdmId: string;
  user: AuthenticatedUser;
  contacts: Contact[];
  selectedProductOffers: ProductOfferingsResponse[];
  /** Pass methods.getValues from the parent useForm() call */
  getFormValues: () => PlaceOrderFormDataCoCo;
  /** Employee (EOE) orders skip the customer step, so the draft order is created on mount and purpose of use is omitted. */
  isEmployeeOrder?: boolean;
  /** Called when the draft order cannot be created on mount */
  onDraftOrderError?: () => void;
}

export interface UseCheckoutOrchestratorResult {
  orderId: string;
  deliveryMethodGroups: DeliveryMethodGroup[] | undefined;
  deliveryOptions: DeliveryOptionsResponse | undefined;
  customerPrimaryAddress: CustomerPrimaryAddress | undefined;
  availableInstallationDates: AvailableInstallationDatesResponseItem[];
  installationAvailability: InstallationAvailabilityState;
  availabilityCheckedDate?: string;
  checkInstallationAvailability: () => Promise<void>;
  confirmInstallationTimeSlots: (timeSlotIds: Record<string, string>) => Promise<void>;
  invalidateInstallationAvailability: () => void;
  stepHandlers: Partial<Record<PlaceOrderStep, () => Promise<void>>>;
}

export const isInstallationTimeSlotSelectionValid = (
  availability: AvailableInstallationDatesResponseItem[],
  timeSlotIds: Record<string, string>
): boolean => {
  const availabilityCartItemIds = new Set(availability.map(result => result.cartItemId));
  const selectedCartItemIds = Object.keys(timeSlotIds);
  return (
    availability.length > 0 &&
    selectedCartItemIds.length === availabilityCartItemIds.size &&
    selectedCartItemIds.every(cartItemId => availabilityCartItemIds.has(cartItemId)) &&
    availability.every(result => {
      const selectedTimeSlotId = timeSlotIds[result.cartItemId];
      return Boolean(selectedTimeSlotId && result.availableTimeSlot.some(slot => slot.id === selectedTimeSlotId));
    })
  );
};

const getValidatedDacInstallationDate = (preferredInstallationDate?: string): string | undefined => {
  if (!preferredInstallationDate) {
    return undefined;
  }

  const dacInstallationDate = toDacInstallationDate(preferredInstallationDate);
  if (!dacInstallationDate) {
    throw new Error('Invalid preferred installation date');
  }

  return dacInstallationDate;
};

const getInstallationAvailabilityRequests = (
  updatedOrder: Awaited<ReturnType<typeof updateProductOrder>>,
  basket: ShoppingBasketType,
  dacInstallationDate: string
) => {
  const installationAvailabilityCartItemIds = new Set(
    basket.productOfferings.filter(shouldShowInstallationDateSelection).map(item => item.cartItemId)
  );

  return updatedOrder.productOrderItems
    .filter(item => installationAvailabilityCartItemIds.has(item.cartItemId))
    .map(item => ({
      cartItemId: item.cartItemId,
      orderItemId: item.id,
      preferredInstallationDate: dacInstallationDate,
    }));
};

/**
 * Encapsulates all step-transition API calls for the CoCo checkout flow.
 *
 * Accepts explicit dependencies instead of reading from context/form hooks,
 * so it can be called at the top level of PlaceOrderCoCo before any providers
 * are rendered. This avoids the hook-inside-provider ordering constraint.
 *
 * Each handler corresponds to one step's "Next" action:
 *  - CUSTOMER  → createProductOrder
 *  - PRODUCTS  → updateProductOrder + fetch delivery methods
 *  - DELIVERY  → updateProductOrderDeliveryMethod
 */
export const useCheckoutOrchestratorCoCo = ({
  basket,
  mdmId,
  user,
  contacts,
  selectedProductOffers,
  getFormValues,
  isEmployeeOrder = false,
  onDraftOrderError,
}: UseCheckoutOrchestratorOptions): UseCheckoutOrchestratorResult => {
  const [orderId, setOrderId] = useState('');
  const draftOrderId = useRef('');
  const draftOrderRequest = useRef<Promise<string> | null>(null);
  const [deliveryMethodGroups, setDeliveryMethodGroups] = useState<DeliveryMethodGroup[] | undefined>();
  const [deliveryOptions, setDeliveryOptions] = useState<DeliveryOptionsResponse | undefined>();
  const [customerPrimaryAddress, setCustomerPrimaryAddress] = useState<CustomerPrimaryAddress | undefined>();
  const [availableInstallationDates, setAvailableInstallationDates] = useState<
    AvailableInstallationDatesResponseItem[]
  >([]);
  const [installationAvailability, setInstallationAvailability] = useState<InstallationAvailabilityState>({
    status: InstallationAvailabilityStatus.IDLE,
    results: [],
  });
  const installationAvailabilityRequestId = useRef(0);
  const availabilityCheckedDate = useRef<string | undefined>(undefined);
  const [availabilityCheckedDateState, setAvailabilityCheckedDateState] = useState<string>();

  const companyName = getCompanyName(user, getActiveAccountMasterId());

  const currentDeliveryAddressId = getFormValues().delivery.deliveryAddressId ?? customerPrimaryAddress?.addressId;

  useEffect(() => {
    if (!orderId || !currentDeliveryAddressId) {
      return;
    }

    let isActive = true;
    fetchDeliveryOptions(orderId, currentDeliveryAddressId)
      .then(response => {
        if (isActive) {
          setDeliveryOptions(response);
        }
      })
      .catch(() => {
        if (isActive) {
          setDeliveryOptions(undefined);
        }
      });

    return () => {
      isActive = false;
    };
  }, [currentDeliveryAddressId, orderId]);

  const ensureDraftOrder = async (): Promise<string> => {
    if (orderId || draftOrderId.current) {
      return orderId || draftOrderId.current;
    }
    if (draftOrderRequest.current) {
      return draftOrderRequest.current;
    }
    if (!user.contact?.contactMasterId || !mdmId) {
      throw new Error('Cannot create a product order without customer and contact identifiers');
    }
    const payload = buildProductOrderRequest(basket, mdmId, user.contact.contactMasterId);
    const request = createProductOrder(payload).then(response => {
      draftOrderId.current = response.id;
      setOrderId(response.id);
      return response.id;
    });
    draftOrderRequest.current = request;
    return request;
  };

  const handleCustomerStep = async () => {
    await ensureDraftOrder();
  };

  const contactMasterId = user.contact?.contactMasterId;
  useEffect(() => {
    if (!isEmployeeOrder || !mdmId || !contactMasterId) {
      return;
    }
    ensureDraftOrder().catch(() => onDraftOrderError?.());
  }, [isEmployeeOrder, mdmId, contactMasterId]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetInstallationAvailabilityState = (status: InstallationAvailabilityStatus) => {
    availabilityCheckedDate.current = undefined;
    setAvailabilityCheckedDateState(undefined);
    setAvailableInstallationDates([]);
    setInstallationAvailability({ status, results: [] });
  };

  const updateDraftOrderFromProductsStep = async (installationTimeSlotIds: Record<string, string> = {}) => {
    const currentOrderId = orderId || draftOrderId.current;
    if (!currentOrderId) {
      throw new Error('Cannot update without a product order');
    }
    const { products } = getFormValues();
    const payload = buildUpdateProductOrderRequest(
      basket,
      products.placeOrderItemUnits,
      selectedProductOffers,
      contacts,
      installationTimeSlotIds,
      { omitPurposeOfUse: isEmployeeOrder }
    );
    await updateProductOrder(currentOrderId, payload);
  };

  const checkInstallationAvailability = async () => {
    const currentOrderId = orderId || draftOrderId.current;
    if (!currentOrderId) {
      return;
    }

    const requestId = ++installationAvailabilityRequestId.current;
    resetInstallationAvailabilityState(InstallationAvailabilityStatus.LOADING);

    const { products } = getFormValues();
    const preferredInstallationDate = products.preferredInstallationDate;

    try {
      const dacInstallationDate = getValidatedDacInstallationDate(preferredInstallationDate);
      const payload = buildUpdateProductOrderRequest(
        basket,
        products.placeOrderItemUnits,
        selectedProductOffers,
        contacts,
        {},
        { omitPurposeOfUse: isEmployeeOrder }
      );
      const updatedOrder = await updateProductOrder(currentOrderId, payload);

      if (requestId !== installationAvailabilityRequestId.current) {
        return;
      }

      if (!dacInstallationDate) {
        resetInstallationAvailabilityState(InstallationAvailabilityStatus.IDLE);
        return;
      }

      const availabilityRequests = getInstallationAvailabilityRequests(updatedOrder, basket, dacInstallationDate);

      if (availabilityRequests.length === 0) {
        availabilityCheckedDate.current = dacInstallationDate;
        setAvailabilityCheckedDateState(dacInstallationDate);
        setAvailableInstallationDates([]);
        setInstallationAvailability({ status: InstallationAvailabilityStatus.EMPTY, results: [] });
        return;
      }

      const results = await fetchAvailableInstallationDates(currentOrderId, availabilityRequests);
      if (requestId !== installationAvailabilityRequestId.current) {
        return;
      }
      const hasAvailableSlots = results.some(result => result.availableTimeSlot.length > 0);
      availabilityCheckedDate.current = dacInstallationDate;
      setAvailabilityCheckedDateState(dacInstallationDate);
      setAvailableInstallationDates(results);
      setInstallationAvailability({
        status: hasAvailableSlots ? InstallationAvailabilityStatus.SUCCESS : InstallationAvailabilityStatus.EMPTY,
        results,
      });
    } catch (error) {
      if (requestId !== installationAvailabilityRequestId.current) {
        return;
      }
      setAvailableInstallationDates([]);
      setInstallationAvailability({
        status: InstallationAvailabilityStatus.ERROR,
        results: [],
        error: error instanceof Error ? error.message : 'Installation availability check failed',
      });
      throw error;
    }
  };

  const invalidateInstallationAvailability = () => {
    installationAvailabilityRequestId.current += 1;
    resetInstallationAvailabilityState(InstallationAvailabilityStatus.IDLE);
  };

  const confirmInstallationTimeSlots = async (timeSlotIds: Record<string, string>) => {
    if (!orderId && !draftOrderId.current) {
      throw new Error('Cannot confirm installation time slots without a product order');
    }
    if (!isInstallationTimeSlotSelectionValid(installationAvailability.results, timeSlotIds)) {
      throw new Error('Invalid installation time slot selection');
    }

    await updateDraftOrderFromProductsStep(timeSlotIds);

    const preferredInstallationDate = getFormValues().products.preferredInstallationDate;
    const dacInstallationDate = preferredInstallationDate
      ? toDacInstallationDate(preferredInstallationDate)
      : undefined;
    availabilityCheckedDate.current = dacInstallationDate;
    setAvailabilityCheckedDateState(dacInstallationDate);
  };

  const updateOrderWithConfirmedInstallationTimeSlots = async (
    preferredInstallationDate: string | undefined,
    confirmedTimeSlotIds: Record<string, string>,
    hasInstallationAvailabilityProduct: boolean
  ) => {
    if (!hasInstallationAvailabilityProduct) {
      await updateDraftOrderFromProductsStep();
      return;
    }

    if (!preferredInstallationDate) {
      throw new Error('An installation date is required for the selected time slot');
    }
    if (availabilityCheckedDate.current !== preferredInstallationDate) {
      throw new Error('Please check installation availability before continuing');
    }
    if (!isInstallationTimeSlotSelectionValid(installationAvailability.results, confirmedTimeSlotIds)) {
      throw new Error('Invalid installation time slot selection');
    }

    await updateDraftOrderFromProductsStep(confirmedTimeSlotIds);
  };

  const updateOrderForProductsStep = async () => {
    const { products } = getFormValues();
    const confirmedTimeSlotIds = products.installationAppointmentTimeSlotIds ?? {};
    const preferredInstallationDate = products.preferredInstallationDate
      ? toDacInstallationDate(products.preferredInstallationDate)
      : undefined;
    const hasInstallationAvailabilityProduct = basket.productOfferings.some(shouldShowInstallationDateSelection);

    if (Object.keys(confirmedTimeSlotIds).length > 0) {
      await updateOrderWithConfirmedInstallationTimeSlots(
        preferredInstallationDate,
        confirmedTimeSlotIds,
        hasInstallationAvailabilityProduct
      );
      return;
    }

    if (hasInstallationAvailabilityProduct) {
      await checkInstallationAvailability();
      throw new Error('Please confirm an installation time slot before continuing');
    }

    await updateDraftOrderFromProductsStep();
  };

  const handleProductsStep = async () => {
    const currentOrderId = orderId || (await ensureDraftOrder());

    await updateOrderForProductsStep();

    const [deliveryMethodsResponse, customerResponse] = await Promise.all([
      fetchProductDeliveryMethods(currentOrderId),
      fetchCustomer(),
    ]);

    setDeliveryMethodGroups(deliveryMethodsResponse);

    const primaryAddress = getCustomerPrimaryAddress(customerResponse);
    if (primaryAddress?.addressId) {
      setCustomerPrimaryAddress({
        deliveryAddress: mapCustomerAddressToDeliveryAddress(primaryAddress, companyName),
        addressId: primaryAddress.addressId,
      });
    }
  };

  const handleDeliveryStep = async () => {
    const { delivery } = getFormValues();
    const selectedMethod = flattenDeliveryOfferings(deliveryMethodGroups).find(m => m.id === delivery.deliveryType);

    if (!selectedMethod || !delivery.deliveryAddress) {
      return;
    }

    const shipmentType = delivery.shipmentType ?? DeliveryOption.TOTAL_DELIVERY;
    const payload = buildDeliveryMethodRequest(
      selectedMethod,
      {
        deliveryAddress: delivery.deliveryAddress,
        addressId: delivery.deliveryAddressId ?? '',
      },
      shipmentType
    );

    await updateProductOrderDeliveryMethod(payload, orderId);
  };

  return {
    orderId,
    deliveryMethodGroups,
    deliveryOptions,
    customerPrimaryAddress,
    availableInstallationDates,
    installationAvailability,
    availabilityCheckedDate: availabilityCheckedDateState,
    checkInstallationAvailability,
    confirmInstallationTimeSlots,
    invalidateInstallationAvailability,
    stepHandlers: {
      [PlaceOrderStep.CUSTOMER]: handleCustomerStep,
      [PlaceOrderStep.PRODUCTS]: handleProductsStep,
      [PlaceOrderStep.DELIVERY]: handleDeliveryStep,
    },
  };
};
